import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useApi } from './useApi';
import { useTheme } from './useTheme';
import { useKeyboardShortcuts, formatKeyForDisplay } from './useKeyboardShortcuts';
import type { Job, CatalogueItem, Supplier, Contact, Document } from '@dbx/shared';

// Search result types
export interface QuickAction {
  id: string;
  title: string;
  icon: string;
  shortcut?: string;
  category: 'navigation' | 'create' | 'action' | 'settings';
  keywords?: string[];
  action: () => void;
}

export interface SearchResult {
  id: string;
  type: 'action' | 'job' | 'catalogue' | 'supplier' | 'order' | 'contact' | 'document' | 'recent';
  title: string;
  subtitle?: string;
  badge?: string;
  badgeClass?: string;
  icon: string;
  shortcut?: string;
  route?: string;
  action?: () => void;
  matchedText?: string; // For highlighting
  score?: number; // For sorting by relevance
}

export interface SearchResults {
  actions: SearchResult[];
  recent: SearchResult[];
  jobs: SearchResult[];
  catalogue: SearchResult[];
  suppliers: SearchResult[];
  orders: SearchResult[];
  contacts: SearchResult[];
  documents: SearchResult[];
}

const RECENT_SEARCHES_KEY = 'dbx-command-bar-recent';
const RECENT_ITEMS_KEY = 'dbx-command-bar-recent-items';
const MAX_RECENT_SEARCHES = 5;
const MAX_RECENT_ITEMS = 10;

// Singleton state - shared across all component instances
const isOpen = ref(false);
const searchQuery = ref('');
const results = ref<SearchResults>({
  actions: [],
  recent: [],
  jobs: [],
  catalogue: [],
  suppliers: [],
  orders: [],
  contacts: [],
  documents: [],
});
const loading = ref(false);
const selectedIndex = ref(0);
const recentSearches = ref<string[]>([]);
const recentItems = ref<SearchResult[]>([]);
const showShortcutsHelp = ref(false);
const searchMode = ref<'all' | 'commands' | 'search'>('all'); // > for commands, no prefix for search

// Debounce timer
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Fuzzy match scoring function
 * Returns a score based on how well the query matches the text
 * Higher score = better match
 */
function fuzzyMatch(text: string, query: string): number {
  if (!query) return 0;

  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();

  // Exact match = highest score
  if (textLower === queryLower) return 1000;

  // Starts with = very high score
  if (textLower.startsWith(queryLower)) return 900;

  // Contains as a word = high score
  const wordBoundary = new RegExp(`\\b${queryLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
  if (wordBoundary.test(text)) return 800;

  // Contains anywhere = medium score
  if (textLower.includes(queryLower)) return 700;

  // Fuzzy match (all characters in order)
  let queryIndex = 0;
  let consecutiveMatches = 0;
  let maxConsecutive = 0;
  let lastMatchIndex = -1;

  for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
    if (textLower[i] === queryLower[queryIndex]) {
      if (lastMatchIndex === i - 1) {
        consecutiveMatches++;
        maxConsecutive = Math.max(maxConsecutive, consecutiveMatches);
      } else {
        consecutiveMatches = 1;
      }
      lastMatchIndex = i;
      queryIndex++;
    }
  }

  // Return score based on how many characters matched and consecutive matches
  if (queryIndex === queryLower.length) {
    return 500 + (maxConsecutive * 10);
  }

  return 0;
}

/**
 * Get the best matching text for highlighting
 */
function getMatchedText(text: string, query: string): string {
  if (!query || !text) return '';

  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  const index = textLower.indexOf(queryLower);

  if (index >= 0) {
    return text.substring(index, index + query.length);
  }

  return '';
}

export function useCommandBar() {
  const router = useRouter();
  const api = useApi();
  const { toggleTheme, isDark, cycleTheme, theme } = useTheme();
  const { getShortcutsByCategory } = useKeyboardShortcuts();

  // Load recent searches from localStorage
  function loadRecentSearches() {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        recentSearches.value = JSON.parse(stored);
      }
    } catch {
      recentSearches.value = [];
    }
  }

  // Load recent items from localStorage
  function loadRecentItems() {
    try {
      const stored = localStorage.getItem(RECENT_ITEMS_KEY);
      if (stored) {
        recentItems.value = JSON.parse(stored);
      }
    } catch {
      recentItems.value = [];
    }
  }

  // Save recent searches to localStorage
  function saveRecentSearches() {
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches.value));
    } catch {
      // Ignore localStorage errors
    }
  }

  // Save recent items to localStorage
  function saveRecentItems() {
    try {
      localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(recentItems.value));
    } catch {
      // Ignore localStorage errors
    }
  }

  // Add a search to recent searches
  function addToRecentSearches(query: string) {
    if (!query.trim()) return;

    // Remove if already exists, then add to front
    const filtered = recentSearches.value.filter(s => s !== query);
    recentSearches.value = [query, ...filtered].slice(0, MAX_RECENT_SEARCHES);
    saveRecentSearches();
  }

  // Add an item to recent items
  function addToRecentItems(result: SearchResult) {
    // Don't add actions to recent items
    if (result.type === 'action' || result.type === 'recent') return;

    // Create a clean copy without the action function (can't serialize)
    const cleanResult: SearchResult = {
      id: result.id,
      type: result.type,
      title: result.title,
      subtitle: result.subtitle,
      badge: result.badge,
      badgeClass: result.badgeClass,
      icon: result.icon,
      route: result.route,
    };

    // Remove if already exists, then add to front
    const filtered = recentItems.value.filter(
      r => !(r.type === cleanResult.type && r.id === cleanResult.id)
    );
    recentItems.value = [cleanResult, ...filtered].slice(0, MAX_RECENT_ITEMS);
    saveRecentItems();
  }

  // Clear recent searches
  function clearRecentSearches() {
    recentSearches.value = [];
    saveRecentSearches();
  }

  // Clear recent items
  function clearRecentItems() {
    recentItems.value = [];
    saveRecentItems();
  }

  // Define all quick actions
  const quickActions: QuickAction[] = [
    // Navigation actions
    {
      id: 'nav-dashboard',
      title: 'Go to Dashboard',
      icon: 'bi-speedometer2',
      shortcut: 'g+d',
      category: 'navigation',
      keywords: ['home', 'overview', 'main'],
      action: () => {
        close();
        router.push('/dashboard');
      },
    },
    {
      id: 'nav-jobs',
      title: 'Go to Jobs',
      icon: 'bi-briefcase',
      shortcut: 'g+j',
      category: 'navigation',
      keywords: ['projects', 'work'],
      action: () => {
        close();
        router.push('/jobs');
      },
    },
    {
      id: 'nav-suppliers',
      title: 'Go to Suppliers',
      icon: 'bi-truck',
      shortcut: 'g+s',
      category: 'navigation',
      keywords: ['vendors', 'contractors'],
      action: () => {
        close();
        router.push('/suppliers');
      },
    },
    {
      id: 'nav-contacts',
      title: 'Go to Contacts',
      icon: 'bi-person-lines-fill',
      shortcut: 'g+c',
      category: 'navigation',
      keywords: ['people', 'clients'],
      action: () => {
        close();
        router.push('/contacts');
      },
    },
    {
      id: 'nav-documents',
      title: 'Go to Documents',
      icon: 'bi-folder2-open',
      shortcut: 'g+f',
      category: 'navigation',
      keywords: ['files', 'attachments', 'uploads'],
      action: () => {
        close();
        router.push('/documents');
      },
    },
    {
      id: 'nav-catalogue',
      title: 'Go to Catalogue',
      icon: 'bi-book',
      shortcut: 'g+l',
      category: 'navigation',
      keywords: ['price list', 'items', 'products'],
      action: () => {
        close();
        router.push('/catalogue');
      },
    },
    {
      id: 'nav-orders',
      title: 'Go to Orders',
      icon: 'bi-cart',
      shortcut: 'g+o',
      category: 'navigation',
      keywords: ['purchase orders', 'po'],
      action: () => {
        close();
        router.push('/orders');
      },
    },
    {
      id: 'nav-accounting',
      title: 'Go to Accounting',
      icon: 'bi-cash-stack',
      shortcut: 'g+a',
      category: 'navigation',
      keywords: ['finance', 'invoices', 'payments', 'ledger'],
      action: () => {
        close();
        router.push('/accounting');
      },
    },
    {
      id: 'nav-reports',
      title: 'Go to Reports',
      icon: 'bi-file-earmark-bar-graph',
      shortcut: 'g+r',
      category: 'navigation',
      keywords: ['analytics', 'charts', 'statistics'],
      action: () => {
        close();
        router.push('/reports');
      },
    },
    {
      id: 'nav-settings',
      title: 'Go to Settings',
      icon: 'bi-gear',
      shortcut: 'g+,',
      category: 'navigation',
      keywords: ['preferences', 'configuration', 'options'],
      action: () => {
        close();
        router.push('/settings');
      },
    },
    {
      id: 'nav-projects',
      title: 'Go to Projects',
      icon: 'bi-kanban',
      shortcut: 'g+p',
      category: 'navigation',
      keywords: ['tasks', 'kanban', 'board'],
      action: () => {
        close();
        router.push('/projects');
      },
    },
    {
      id: 'nav-transmittals',
      title: 'Go to Transmittals',
      icon: 'bi-send',
      shortcut: 'g+t',
      category: 'navigation',
      keywords: ['share', 'send documents'],
      action: () => {
        close();
        router.push('/transmittals');
      },
    },
    // Create actions
    {
      id: 'create-job',
      title: 'Create New Job',
      icon: 'bi-plus-circle',
      shortcut: 'n+j',
      category: 'create',
      keywords: ['new project', 'add job'],
      action: () => {
        close();
        router.push('/jobs?action=create');
      },
    },
    {
      id: 'create-supplier',
      title: 'Create New Supplier',
      icon: 'bi-plus-circle',
      shortcut: 'n+s',
      category: 'create',
      keywords: ['new vendor', 'add supplier'],
      action: () => {
        close();
        router.push('/suppliers?action=create');
      },
    },
    {
      id: 'create-contact',
      title: 'Create New Contact',
      icon: 'bi-plus-circle',
      shortcut: 'n+c',
      category: 'create',
      keywords: ['new person', 'add contact'],
      action: () => {
        close();
        router.push('/contacts?action=create');
      },
    },
    {
      id: 'upload-document',
      title: 'Upload Document',
      icon: 'bi-cloud-upload',
      shortcut: 'n+d',
      category: 'create',
      keywords: ['new file', 'add document', 'upload file'],
      action: () => {
        close();
        router.push('/documents?action=upload');
      },
    },
    {
      id: 'create-order',
      title: 'Create New Order',
      icon: 'bi-plus-circle',
      shortcut: 'n+o',
      category: 'create',
      keywords: ['new purchase order', 'add po'],
      action: () => {
        close();
        router.push('/orders?action=create');
      },
    },
    // General actions
    {
      id: 'toggle-theme',
      title: 'Toggle Dark Mode',
      icon: isDark.value ? 'bi-sun' : 'bi-moon',
      shortcut: 'Ctrl+Shift+L',
      category: 'action',
      keywords: ['dark mode', 'light mode', 'theme', 'appearance'],
      action: () => {
        toggleTheme();
        close();
      },
    },
    {
      id: 'cycle-theme',
      title: 'Cycle Theme (Light/Dark/System)',
      icon: 'bi-circle-half',
      category: 'action',
      keywords: ['theme', 'appearance', 'system'],
      action: () => {
        cycleTheme();
        close();
      },
    },
    {
      id: 'show-shortcuts',
      title: 'Show Keyboard Shortcuts',
      icon: 'bi-keyboard',
      shortcut: '?',
      category: 'action',
      keywords: ['help', 'hotkeys', 'keybindings'],
      action: () => {
        showShortcutsHelp.value = true;
        close();
      },
    },
    {
      id: 'refresh-data',
      title: 'Refresh Data',
      icon: 'bi-arrow-clockwise',
      shortcut: 'Ctrl+Shift+R',
      category: 'action',
      keywords: ['reload', 'update', 'sync'],
      action: () => {
        close();
        window.location.reload();
      },
    },
    {
      id: 'export-csv',
      title: 'Export Current View to CSV',
      icon: 'bi-file-earmark-spreadsheet',
      shortcut: 'Ctrl+Shift+E',
      category: 'action',
      keywords: ['download', 'excel', 'spreadsheet'],
      action: () => {
        // Dispatch a custom event that views can listen for
        window.dispatchEvent(new CustomEvent('command-bar:export-csv'));
        close();
      },
    },
    {
      id: 'copy-url',
      title: 'Copy Current URL',
      icon: 'bi-link-45deg',
      category: 'action',
      keywords: ['share', 'link', 'clipboard'],
      action: async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          // Could add toast notification here
        } catch (e) {
          console.error('Failed to copy URL:', e);
        }
        close();
      },
    },
    // Settings quick actions
    {
      id: 'settings-company',
      title: 'Company Settings',
      icon: 'bi-building',
      category: 'settings',
      keywords: ['organization', 'tenant'],
      action: () => {
        close();
        router.push('/settings?tab=company');
      },
    },
    {
      id: 'settings-users',
      title: 'User Management',
      icon: 'bi-people',
      category: 'settings',
      keywords: ['permissions', 'access', 'team'],
      action: () => {
        close();
        router.push('/settings?tab=users');
      },
    },
  ];

  // Get theme icon dynamically
  function getThemeIcon(): string {
    if (theme.value === 'system') return 'bi-circle-half';
    return isDark.value ? 'bi-sun' : 'bi-moon';
  }

  // Get filtered quick actions based on query
  function getFilteredActions(query: string): SearchResult[] {
    const lowerQuery = query.toLowerCase().trim();

    // Update theme icon dynamically
    const actionsWithUpdatedTheme = quickActions.map(a => {
      if (a.id === 'toggle-theme') {
        return { ...a, icon: getThemeIcon() };
      }
      return a;
    });

    if (!lowerQuery) {
      // Return all actions when no query, sorted by category
      return actionsWithUpdatedTheme.map(a => ({
        id: a.id,
        type: 'action' as const,
        title: a.title,
        icon: a.icon,
        shortcut: a.shortcut,
        action: a.action,
        score: 0,
      }));
    }

    // Check if it's a command mode query (starts with >)
    const isCommandMode = lowerQuery.startsWith('>');
    const effectiveQuery = isCommandMode ? lowerQuery.slice(1).trim() : lowerQuery;

    return actionsWithUpdatedTheme
      .map(a => {
        // Build searchable text from title and keywords
        const searchText = [a.title, ...(a.keywords || [])].join(' ');
        const score = fuzzyMatch(searchText, effectiveQuery);
        const matchedText = getMatchedText(a.title, effectiveQuery);

        return {
          id: a.id,
          type: 'action' as const,
          title: a.title,
          icon: a.icon,
          shortcut: a.shortcut,
          action: a.action,
          matchedText,
          score,
        };
      })
      .filter(a => a.score > 0)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, isCommandMode ? 20 : 5);
  }

  // Get filtered recent items
  function getFilteredRecentItems(query: string): SearchResult[] {
    if (!query) {
      return recentItems.value.map(r => ({ ...r, type: 'recent' as const }));
    }

    return recentItems.value
      .map(r => {
        const searchText = `${r.title} ${r.subtitle || ''}`;
        const score = fuzzyMatch(searchText, query);
        return { ...r, type: 'recent' as const, score };
      })
      .filter(r => (r.score || 0) > 0)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 3);
  }

  // Perform search
  async function search(query: string) {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Detect search mode
    if (query.startsWith('>')) {
      searchMode.value = 'commands';
    } else {
      searchMode.value = query.trim() ? 'search' : 'all';
    }

    if (!query.trim()) {
      results.value = {
        actions: getFilteredActions(''),
        recent: recentItems.value.slice(0, 5).map(r => ({ ...r, type: 'recent' as const })),
        jobs: [],
        catalogue: [],
        suppliers: [],
        orders: [],
        contacts: [],
        documents: [],
      };
      loading.value = false;
      return;
    }

    // If command mode, only show actions
    if (searchMode.value === 'commands') {
      results.value = {
        actions: getFilteredActions(query),
        recent: [],
        jobs: [],
        catalogue: [],
        suppliers: [],
        orders: [],
        contacts: [],
        documents: [],
      };
      loading.value = false;
      return;
    }

    // Debounce
    searchTimeout = setTimeout(async () => {
      loading.value = true;
      selectedIndex.value = 0;

      try {
        // Run all searches in parallel
        const [jobsResult, catalogueResult, suppliersResult, ordersResult, contactsResult, documentsResult] = await Promise.all([
          api.jobs.getList({ search: query, limit: 10 }).then((jobs: Job[]) =>
            jobs.filter((j: Job) => {
              const searchText = `${j.jobNo} ${j.name || ''} ${j.clientName || ''} ${j.siteAddress || ''} ${j.siteSuburb || ''} ${j.siteState || ''} ${j.sitePostcode || ''}`.toLowerCase();
              return query.toLowerCase().split(/\s+/).every(word => searchText.includes(word));
            }).slice(0, 5)
          ).catch(() => [] as Job[]),
          api.catalogue.getItems({ search: query, limit: 5 }).catch(() => [] as CatalogueItem[]),
          api.suppliers.getList({ search: query }).then((list: Supplier[]) => list.slice(0, 5)).catch(() => [] as Supplier[]),
          api.orders.search(query, 5).catch(() => [] as Array<{ orderNumber: string; supplierName: string | null; status: string; jobNo: string }>),
          api.contacts.getList({ search: query }).then((list: Contact[]) => list.slice(0, 5)).catch(() => [] as Contact[]),
          api.documents.search({ q: query, limit: 5 }).then(res => res.documents || []).catch(() => [] as Document[]),
        ]);

        results.value = {
          actions: getFilteredActions(query).slice(0, 3),
          recent: getFilteredRecentItems(query),
          jobs: jobsResult.map((j: Job) => {
            // Build subtitle with name and address
            const parts: string[] = [];
            if (j.name) parts.push(j.name);
            const address = [j.siteAddress, j.siteSuburb].filter(Boolean).join(', ');
            if (address && address !== j.name) parts.push(address);
            const subtitle = parts.join(' - ') || undefined;
            const searchText = `${j.jobNo} ${j.name || ''}`;
            const score = fuzzyMatch(searchText, query);

            return {
              id: j.jobNo,
              type: 'job' as const,
              title: j.jobNo,
              subtitle,
              badge: j.status,
              badgeClass: getStatusBadgeClass(j.status),
              icon: 'bi-briefcase',
              route: `/jobs?select=${encodeURIComponent(j.jobNo)}`,
              matchedText: getMatchedText(j.jobNo, query) || getMatchedText(j.name || '', query),
              score,
            };
          }),
          catalogue: catalogueResult.map((c: CatalogueItem) => {
            const searchText = `${c.priceCode} ${c.description}`;
            const score = fuzzyMatch(searchText, query);
            return {
              id: c.priceCode,
              type: 'catalogue' as const,
              title: c.priceCode,
              subtitle: c.description,
              badge: c.costCentreCode || undefined,
              badgeClass: 'bg-secondary',
              icon: 'bi-book',
              route: `/catalogue?select=${encodeURIComponent(c.priceCode)}`,
              matchedText: getMatchedText(c.priceCode, query) || getMatchedText(c.description, query),
              score,
            };
          }),
          suppliers: suppliersResult.map((s: Supplier) => {
            const searchText = `${s.code} ${s.name}`;
            const score = fuzzyMatch(searchText, query);
            return {
              id: s.code,
              type: 'supplier' as const,
              title: s.code,
              subtitle: s.name,
              badge: s.area || undefined,
              badgeClass: 'bg-info',
              icon: 'bi-truck',
              route: `/suppliers?select=${encodeURIComponent(s.code)}`,
              matchedText: getMatchedText(s.code, query) || getMatchedText(s.name, query),
              score,
            };
          }),
          orders: ordersResult.map((o: { orderNumber: string; supplierName: string | null; status: string; jobNo: string }) => {
            const searchText = `${o.orderNumber} ${o.supplierName || ''} ${o.jobNo}`;
            const score = fuzzyMatch(searchText, query);
            return {
              id: o.orderNumber,
              type: 'order' as const,
              title: o.orderNumber,
              subtitle: o.supplierName || o.jobNo,
              badge: o.status,
              badgeClass: getOrderStatusBadgeClass(o.status),
              icon: 'bi-cart',
              route: `/orders?select=${encodeURIComponent(o.orderNumber)}`,
              matchedText: getMatchedText(o.orderNumber, query),
              score,
            };
          }),
          contacts: contactsResult.map((c: Contact) => {
            const searchText = `${c.name} ${c.code} ${c.contactPerson || ''}`;
            const score = fuzzyMatch(searchText, query);
            return {
              id: c.code,
              type: 'contact' as const,
              title: c.name,
              subtitle: c.code + (c.contactPerson ? ` - ${c.contactPerson}` : ''),
              badge: c.isSupplier ? 'Supplier' : c.isDebtor ? 'Client' : 'General',
              badgeClass: c.isSupplier ? 'bg-primary' : c.isDebtor ? 'bg-success' : 'bg-secondary',
              icon: c.isSupplier ? 'bi-building' : c.isDebtor ? 'bi-person-fill' : 'bi-person',
              route: `/contacts?select=${encodeURIComponent(c.code)}`,
              matchedText: getMatchedText(c.name, query) || getMatchedText(c.code, query),
              score,
            };
          }),
          documents: documentsResult.map((d: Document) => {
            const searchText = `${d.fileName} ${d.description || ''}`;
            const score = fuzzyMatch(searchText, query);
            return {
              id: d.id,
              type: 'document' as const,
              title: d.fileName,
              subtitle: d.description || d.discipline || undefined,
              badge: d.status || undefined,
              badgeClass: getDocumentStatusBadgeClass(d.status),
              icon: getDocumentIcon(d.mimeType),
              route: `/documents?select=${encodeURIComponent(d.id)}`,
              matchedText: getMatchedText(d.fileName, query),
              score,
            };
          }),
        };
      } catch (error) {
        console.error('Command bar search error:', error);
      } finally {
        loading.value = false;
      }
    }, 250);
  }

  // Get badge class for job status
  function getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'in progress':
        return 'bg-success';
      case 'quote':
      case 'estimating':
        return 'bg-warning text-dark';
      case 'completed':
      case 'closed':
        return 'bg-secondary';
      case 'archived':
        return 'bg-dark';
      default:
        return 'bg-primary';
    }
  }

  // Get badge class for order status
  function getOrderStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'ordered':
      case 'sent':
        return 'bg-success';
      case 'draft':
        return 'bg-warning text-dark';
      case 'cancelled':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  // Get badge class for document status
  function getDocumentStatusBadgeClass(status: string | undefined): string {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-success';
      case 'pending':
      case 'for review':
        return 'bg-warning text-dark';
      case 'rejected':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  // Get document icon based on file type
  function getDocumentIcon(fileType: string | undefined): string {
    const type = fileType?.toLowerCase() || '';
    if (type.includes('pdf')) return 'bi-file-earmark-pdf';
    if (type.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].some(ext => type.includes(ext))) return 'bi-file-earmark-image';
    if (type.includes('word') || type.includes('doc')) return 'bi-file-earmark-word';
    if (type.includes('excel') || type.includes('spreadsheet') || type.includes('xls')) return 'bi-file-earmark-excel';
    if (type.includes('powerpoint') || type.includes('ppt')) return 'bi-file-earmark-ppt';
    if (type.includes('zip') || type.includes('rar') || type.includes('7z')) return 'bi-file-earmark-zip';
    if (type.includes('dwg') || type.includes('dxf')) return 'bi-rulers';
    if (type.includes('ifc') || type.includes('rvt')) return 'bi-box';
    return 'bi-file-earmark';
  }

  // Get all results as flat array for navigation
  const allResults = computed(() => {
    return [
      ...results.value.actions,
      ...results.value.recent,
      ...results.value.jobs,
      ...results.value.catalogue,
      ...results.value.suppliers,
      ...results.value.contacts,
      ...results.value.documents,
      ...results.value.orders,
    ];
  });

  // Open command bar
  function open() {
    isOpen.value = true;
    searchQuery.value = '';
    selectedIndex.value = 0;
    searchMode.value = 'all';
    loadRecentSearches();
    loadRecentItems();
    results.value = {
      actions: getFilteredActions(''),
      recent: recentItems.value.slice(0, 5).map(r => ({ ...r, type: 'recent' as const })),
      jobs: [],
      catalogue: [],
      suppliers: [],
      orders: [],
      contacts: [],
      documents: [],
    };
  }

  // Close command bar
  function close() {
    isOpen.value = false;
    searchQuery.value = '';
    searchMode.value = 'all';
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  }

  // Toggle command bar
  function toggle() {
    if (isOpen.value) {
      close();
    } else {
      open();
    }
  }

  // Navigate up in results
  function navigateUp() {
    if (selectedIndex.value > 0) {
      selectedIndex.value--;
    }
  }

  // Navigate down in results
  function navigateDown() {
    if (selectedIndex.value < allResults.value.length - 1) {
      selectedIndex.value++;
    }
  }

  // Select current result
  function selectCurrent() {
    const result = allResults.value[selectedIndex.value];
    if (result) {
      selectResult(result);
    }
  }

  // Select a specific result
  function selectResult(result: SearchResult) {
    // Add to recent searches
    if (searchQuery.value.trim()) {
      addToRecentSearches(searchQuery.value.trim());
    }

    // Add to recent items (for non-action results)
    addToRecentItems(result);

    if (result.action) {
      result.action();
    } else if (result.route) {
      router.push(result.route);
      close();
    }
  }

  // Handle global keyboard events
  function handleKeydown(event: KeyboardEvent) {
    // Ctrl+K or Cmd+K to toggle
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault();
      toggle();
      return;
    }

    // Ctrl+Shift+P for command mode (like VS Code)
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'p') {
      event.preventDefault();
      open();
      searchQuery.value = '>';
      return;
    }

    // Only handle other keys if command bar is open
    if (!isOpen.value) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'ArrowUp':
        event.preventDefault();
        navigateUp();
        break;
      case 'ArrowDown':
        event.preventDefault();
        navigateDown();
        break;
      case 'Enter':
        event.preventDefault();
        selectCurrent();
        break;
    }
  }

  // Get shortcuts for help display
  function getShortcutsForHelp() {
    return getShortcutsByCategory();
  }

  // Set up keyboard listener on mount
  onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
    loadRecentSearches();
    loadRecentItems();
  });

  // Clean up on unmount
  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
  });

  return {
    // State
    isOpen,
    searchQuery,
    results,
    loading,
    selectedIndex,
    recentSearches,
    recentItems,
    allResults,
    searchMode,
    showShortcutsHelp,

    // Methods
    open,
    close,
    toggle,
    search,
    navigateUp,
    navigateDown,
    selectCurrent,
    selectResult,
    clearRecentSearches,
    clearRecentItems,
    getShortcutsForHelp,
    formatKeyForDisplay,
  };
}
